import React from "react";
import {
	Button,
	ClassNameProps,
	Column,
	Conditional,
	Else,
	Heading,
	If,
	makeClassName,
	Row,
	StyleProps,
	Tooltip,
	TooltipButton,
	TruncatedText,
	useSync,
} from "@hex-insights/core";
import { useActivePageRegistration } from "@hex-insights/router";
import {
	ContractComponents,
	ContractInstallmentComponents,
	ContractInstallmentUtilities,
	ContractListQueryVariables,
	ContractOrderField,
	ContractUtilities,
	EmptyView,
	Icon,
	ModelUtilities,
	OrderDirection,
	PaymentComponents,
	Student,
	StudentComponents,
	StudentFormat,
	StudentNamesAndImagesQuery,
	Tile,
	useContractListQuery,
	useStudentNamesAndImagesQuery,
} from "@hex-insights/verita-romania.shared";
import { Main } from "../../Components";
import { financialsPageInfo } from "./pageinfo";
import styles from "./styles.module.css";

export function FinancialsPage() {
	useActivePageRegistration(financialsPageInfo);

	const { loading: loadingStudents, data: studentData } = useStudentNamesAndImagesQuery({
		variables: {
			filters: {
				relationships: [{ guardian: [{ person: [{ user: [{ isCurrentEQ: true }] }] }] }],
			},
		},
	});

	const [studentID, setStudentID] = React.useState<Student["id"] | null>(null);

	const studentIDs = React.useMemo(() => {
		if (studentID) {
			return [studentID];
		}
		return studentData?.studentConnection.edges.map(({ node }) => node.id) ?? [];
	}, [studentID, studentData]);

	return (
		<Main style={{ display: "flex", flexDirection: "column" }}>
			<Row justify="space-between" align="center" style={{ margin: "var(--heading--level-1---margin)" }}>
				<Row justify="spaced-start" align="center" horizontalSpacing="1rem">
					<Heading.H1 noMargin>Financials</Heading.H1>
					<StudentSelect
						isLoading={loadingStudents}
						studentID={studentID}
						setStudentID={setStudentID}
						studentData={studentData}
					/>
				</Row>

				<PaymentComponents.PaymentGuideModalController />
			</Row>

			<Conditional>
				<If condition={studentIDs.length > 0}>
					<FinancialsBody studentIDs={studentIDs} />
				</If>
				<If condition={loadingStudents}>
					<span>Loading...</span>
				</If>
				<Else>
					<Row justify="spaced-start" style={{ flexGrow: 1 }}>
						<EmptyView
							IconComponent={Icon.Money}
							primaryText="Financials"
							secondaryText="No financial records found."
						/>
					</Row>
				</Else>
			</Conditional>
		</Main>
	);
}

type StudentSelectProps = {
	isLoading?: boolean;
	studentID: Student["id"] | null;
	setStudentID: (v: Student["id"] | null) => void;
	studentData: StudentNamesAndImagesQuery | null | undefined;
};

function StudentSelect({ isLoading = false, studentID, setStudentID, studentData }: StudentSelectProps) {
	// TODO refactor
	const students = React.useMemo(() => {
		return studentData?.studentConnection.edges.map((e) => e.node) ?? [];
	}, [studentData]);

	const selectedStudent = studentData?.studentConnection.edges.find((e) => e.node.id === studentID)?.node;

	const { syncID, synchronize } = useSync();
	const onSelectStudent = React.useCallback(
		(studentID: Student["id"] | null) => {
			setStudentID(studentID);
			synchronize();
		},
		[setStudentID, synchronize],
	);

	return (
		<TooltipButton side="bottom" closeID={syncID}>
			{React.useCallback(() => {
				return (
					<Tooltip isInteractable style={{ width: "18rem" }}>
						<Tooltip.Body>
							<Column justify="spaced-start" verticalSpacing="0.375rem">
								<Button
									onClick={() => onSelectStudent(null)}
									className={makeClassName(
										styles["student-option-button"],
										!studentID ? styles["student-option-button--selected"] : "",
									)}
								>
									<Row justify="spaced-start" align="center">
										<StudentComponents.StudentMultiIcon students={students} size={32} />
										All Students
									</Row>
								</Button>
								{students.map((student) => (
									<Button
										key={student.id}
										onClick={() => onSelectStudent(student.id)}
										className={makeClassName(
											styles["student-option-button"],
											studentID === student.id ? styles["student-option-button--selected"] : "",
										)}
									>
										<Row justify="spaced-start" align="center">
											<StudentComponents.StudentIcon student={student} size={32} />
											<TruncatedText>{StudentFormat.name(student)}</TruncatedText>
										</Row>
									</Button>
								))}
							</Column>
						</Tooltip.Body>
					</Tooltip>
				);
			}, [studentID, onSelectStudent, students])}
			{React.useCallback(
				({ onClick }) => {
					return (
						<Button
							variant="tertiary"
							onClick={onClick}
							disabled={isLoading || students.length === 0}
							className={styles["student-options-tooltip-button"]}
						>
							<Conditional>
								<If condition={isLoading}>
									<span>Loading...</span>
								</If>
								<If condition={students.length === 0}>
									<span>No students found</span>
								</If>
								<Else>
									<Row justify="spaced-start" align="center">
										<Conditional>
											<If condition={!studentID}>
												<Row justify="spaced-start" align="center">
													<StudentComponents.StudentMultiIcon students={students} size={32} />
													All Students
												</Row>
											</If>
											<Else>
												{!!selectedStudent && (
													<Row justify="spaced-start" align="center">
														<StudentComponents.StudentIcon student={selectedStudent} size={32} />
														<TruncatedText>{StudentFormat.name(selectedStudent)}</TruncatedText>
													</Row>
												)}
											</Else>
										</Conditional>
										<Icon.CaretDown size={16} color="var(--neutral-colors--tertiary)" />
									</Row>
								</Else>
							</Conditional>
						</Button>
					);
				},
				[isLoading, studentID, selectedStudent, students],
			)}
		</TooltipButton>
	);
}

export type FinancialsBodyProps = {
	studentIDs: Student["id"][];
};

export function FinancialsBody({ studentIDs }: FinancialsBodyProps) {
	return (
		<Row justify="spaced-start" style={{ flexGrow: 1 }}>
			<Column justify="spaced-start" style={{ width: "25rem", flexShrink: 0 }}>
				<SummarySection studentIDs={studentIDs} />
				<ContractsSection studentIDs={studentIDs} />
			</Column>
			<Column justify="spaced-start" style={{ flexGrow: 1, minWidth: 0 }}>
				<PaymentProgressSection studentIDs={studentIDs} />
				<MainContentSection studentIDs={studentIDs} style={{ flexGrow: 1 }} />
			</Column>
		</Row>
	);
}

function getContractListQueryVariables(studentIDs: Student["id"][]): ContractListQueryVariables {
	return {
		filters: { studentIDIn: studentIDs },
		order: { field: ContractOrderField.EffectiveDate, direction: OrderDirection.Desc },
	};
}

type PageSectionProps = {
	studentIDs: Student["id"][];
} & Partial<ClassNameProps & StyleProps>;

function SummarySection({ studentIDs, className, style }: PageSectionProps) {
	const { loading, data } = useContractListQuery({
		variables: getContractListQueryVariables(studentIDs),
	});

	return (
		<ContractComponents.ContractDueSummaryTile
			variant="parent"
			headingLevel={3}
			isLoading={loading}
			contracts={data?.contractConnection.edges}
			className={className}
			style={style}
		/>
	);
}

function ContractsSection({ studentIDs, className, style }: PageSectionProps) {
	const { loading, data } = useContractListQuery({
		variables: getContractListQueryVariables(studentIDs),
	});

	return (
		<ContractComponents.ContractListSection
			variant="parent"
			isLoading={loading}
			contracts={data?.contractConnection.edges}
			Container={Tile}
			headingLevel={3}
			emptyView="full"
			className={className}
			style={style}
		/>
	);
}

function PaymentProgressSection({ studentIDs, className, style }: PageSectionProps) {
	const { data } = useContractListQuery({
		variables: getContractListQueryVariables(studentIDs),
	});
	const paymentProgress = React.useMemo(() => {
		if (!data) {
			return ContractUtilities.blankPaymentProgressInformation;
		}
		const paymentProgresses = data.contractConnection.edges.map(({ node }) =>
			ContractUtilities.calculatePaymentProgress(node),
		);
		return ContractUtilities.mergePaymentProgresses(paymentProgresses);
	}, [data]);

	const contractInstallmentGroups = React.useMemo(() => {
		if (!data) {
			return {
				[ModelUtilities.PaymentStatus.Paid]: [],
				[ModelUtilities.PaymentStatus.Overdue]: [],
				[ModelUtilities.PaymentStatus.Due]: [],
				[ModelUtilities.PaymentStatus.Upcoming]: [],
			};
		}

		const precomputedContractInstallments = ContractUtilities.precomputeContractInstallments(
			data.contractConnection.edges,
		);
		const contractInstallmentGroups = ContractInstallmentUtilities.groupContractInstallmentsByPaymentStatus(
			precomputedContractInstallments,
		);
		return contractInstallmentGroups;
	}, [data]);

	return (
		<Tile className={className} style={style}>
			<Tile.Header>
				<Heading.H3 noMargin>Payment Progress</Heading.H3>
			</Tile.Header>
			<Tile.Body>
				<Column justify="spaced-start" verticalSpacing="1rem">
					<Column justify="spaced-start" verticalSpacing="0.5rem">
						<ContractComponents.ContractPaymentProgressBar paymentProgress={paymentProgress} />
						<ContractComponents.ContractPaymentProgressText paymentProgress={paymentProgress} />
					</Column>

					<ContractInstallmentComponents.ContractInstallmentsPaymentStatusSummaryButtonGrid
						variant="parent"
						paidContractInstallments={contractInstallmentGroups[ModelUtilities.PaymentStatus.Paid]}
						overdueContractInstallments={contractInstallmentGroups[ModelUtilities.PaymentStatus.Overdue]}
						dueContractInstallments={contractInstallmentGroups[ModelUtilities.PaymentStatus.Due]}
						upcomingContractInstallments={contractInstallmentGroups[ModelUtilities.PaymentStatus.Upcoming]}
					/>
				</Column>
			</Tile.Body>
		</Tile>
	);
}

function MainContentSection({ studentIDs, className, style }: PageSectionProps) {
	const { data } = useContractListQuery({
		variables: getContractListQueryVariables(studentIDs),
	});

	const precomputedContractInstallments = React.useMemo(() => {
		if (!data) {
			return [];
		}
		return ContractUtilities.precomputeContractInstallments(data.contractConnection.edges);
	}, [data]);

	return (
		<Tile className={className} style={style}>
			<Tile.Header>
				<Heading.H3 noMargin>Payment Schedule</Heading.H3>
			</Tile.Header>
			<Tile.Body>
				<ContractInstallmentComponents.ContractInstallmentsSummaryAccordion
					variant="parent"
					contractInstallments={precomputedContractInstallments}
				/>
			</Tile.Body>
		</Tile>
	);
}
